import React from "react";
import styled from "styled-components";
import { colors } from "../theme/colors";
import { shadows } from "../theme/shadows";
import { gradients } from "../theme/gradients";

const SectionRibbon = ({
  title,
  count,
  countLabel = "Users",
  icon: Icon,
  variant = "primary",
  className,
  rightContent,
  ...props
}) => {
  return (
    <RibbonContainer className={className} variant={variant} {...props}>
      <RibbonContent>
        <LeftSection>
          {Icon && (
            <IconWrapper variant={variant}>
              <Icon />
            </IconWrapper>
          )}
          <TitleSection>
            <Title variant={variant}>{title}</Title>
            {count !== undefined && (
              <CountSection>
                <CountIcon variant={variant}>
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 16 16"
                    fill="currentColor"
                  >
                    <path d="M7 14s-1 0-1-1 1-4 5-4 5 3 5 4-1 1-1 1H7zm4-6a3 3 0 1 0 0-6 3 3 0 0 0 0 6z" />
                    <path
                      fillRule="evenodd"
                      d="M5.216 14A2.238 2.238 0 0 1 5 13c0-1.355.68-2.75 1.936-3.72A6.325 6.325 0 0 0 5 9c-4 0-5 3-5 4s1 1 1 1h4.216z"
                    />
                    <path d="M4.5 8a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5z" />
                  </svg>
                </CountIcon>
                <CountText variant={variant}>
                  {countLabel}: {count}
                </CountText>
              </CountSection>
            )}
          </TitleSection>
        </LeftSection>

        {rightContent && <RightSection>{rightContent}</RightSection>}
      </RibbonContent>

      <RibbonGlow variant={variant} />
    </RibbonContainer>
  );
};

const RibbonContainer = styled.div`
  position: relative;
  width: 100%;
  margin: 8px 0 16px 0;
  border-radius: 30px;
  overflow: hidden;

  background: ${({ variant }) => {
    switch (variant) {
      case "unaffiliated":
        return "linear-gradient(135deg, rgba(149, 165, 166, 0.12) 0%, rgba(127, 140, 141, 0.08) 100%)";
      case "subtle":
        return gradients.glass;
      case "primary":
      default:
        return "linear-gradient(135deg, rgba(44, 90, 160, 0.12) 0%, rgba(52, 152, 219, 0.08) 100%)";
    }
  }};

  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);

  border: 1px solid
    ${({ variant }) => {
      switch (variant) {
        case "unaffiliated":
          return "rgba(149, 165, 166, 0.25)";
        case "subtle":
          return colors.glass.border;
        case "primary":
        default:
          return "rgba(44, 90, 160, 0.25)";
      }
    }};

  box-shadow: ${({ variant }) => {
    switch (variant) {
      case "unaffiliated":
        return "0 8px 32px rgba(149, 165, 166, 0.15)";
      case "subtle":
        return shadows.md;
      case "primary":
      default:
        return "0 8px 32px rgba(44, 90, 160, 0.15)";
    }
  }};

  transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
  transform: translateZ(0);

  &:hover {
    transform: translateY(-2px);

    background: ${({ variant }) => {
      switch (variant) {
        case "unaffiliated":
          return "linear-gradient(135deg, rgba(149, 165, 166, 0.18) 0%, rgba(127, 140, 141, 0.12) 100%)";
        case "subtle":
          return gradients.glassStrong;
        case "primary":
        default:
          return "linear-gradient(135deg, rgba(44, 90, 160, 0.18) 0%, rgba(52, 152, 219, 0.12) 100%)";
      }
    }};

    border-color: ${({ variant }) => {
      switch (variant) {
        case "unaffiliated":
          return "rgba(149, 165, 166, 0.35)";
        case "subtle":
          return colors.glass.borderStrong;
        case "primary":
        default:
          return "rgba(44, 90, 160, 0.35)";
      }
    }};

    box-shadow: ${({ variant }) => {
      switch (variant) {
        case "unaffiliated":
          return "0 12px 48px rgba(149, 165, 166, 0.2)";
        case "subtle":
          return shadows.lg;
        case "primary":
        default:
          return "0 12px 48px rgba(44, 90, 160, 0.2)";
      }
    }};
  }

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 1px;
    background: linear-gradient(
      90deg,
      transparent,
      rgba(255, 255, 255, 0.6),
      transparent
    );
    z-index: 2;
  }

  @media screen and (max-width: 768px) {
    margin: 16px 0 12px 0;
  }
`;

const RibbonContent = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 24px;
  position: relative;
  z-index: 1;

  @media screen and (max-width: 768px) {
    padding: 16px 20px;
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
  }
`;

const LeftSection = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  flex: 1;

  @media screen and (max-width: 768px) {
    width: 100%;
  }
`;

const IconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  border-radius: 18px;

  background: ${({ variant }) => {
    switch (variant) {
      case "unaffiliated":
        return "linear-gradient(135deg, rgba(149, 165, 166, 0.2) 0%, rgba(127, 140, 141, 0.15) 100%)";
      case "subtle":
        return colors.glass.whiteStrong;
      case "primary":
      default:
        return "linear-gradient(135deg, rgba(44, 90, 160, 0.2) 0%, rgba(52, 152, 219, 0.15) 100%)";
    }
  }};

  color: ${({ variant }) => {
    switch (variant) {
      case "unaffiliated":
        return colors.neutral[600];
      case "subtle":
        return colors.primary.main;
      case "primary":
      default:
        return colors.primary.main;
    }
  }};

  border: 1px solid
    ${({ variant }) => {
      switch (variant) {
        case "unaffiliated":
          return "rgba(149, 165, 166, 0.3)";
        case "subtle":
          return colors.glass.borderStrong;
        case "primary":
        default:
          return "rgba(44, 90, 160, 0.3)";
      }
    }};

  box-shadow: inset 0 1px 2px rgba(255, 255, 255, 0.2);

  svg {
    width: 20px;
    height: 20px;
  }

  @media screen and (max-width: 768px) {
    width: 40px;
    height: 40px;

    svg {
      width: 16px;
      height: 16px;
    }
  }
`;

const TitleSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex: 1;
`;

const Title = styled.h3`
  margin: 0;
  font-size: 1.4em;
  font-weight: 700;

  color: ${({ variant }) => {
    switch (variant) {
      case "unaffiliated":
        return colors.neutral[700];
      case "subtle":
        return colors.primary.main;
      case "primary":
      default:
        return colors.primary.main;
    }
  }};

  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);

  @media screen and (max-width: 768px) {
    font-size: 1.2em;
  }
`;

const CountSection = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const CountIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;

  color: ${({ variant }) => {
    switch (variant) {
      case "unaffiliated":
        return colors.neutral[600];
      case "subtle":
        return colors.primary.light;
      case "primary":
      default:
        return colors.primary.light;
    }
  }};

  svg {
    width: 16px;
    height: 16px;
  }
`;

const CountText = styled.span`
  font-size: 1em;
  font-weight: 600;

  color: ${({ variant }) => {
    switch (variant) {
      case "unaffiliated":
        return colors.neutral[600];
      case "subtle":
        return colors.primary.light;
      case "primary":
      default:
        return colors.primary.light;
    }
  }};

  @media screen and (max-width: 768px) {
    font-size: 0.9em;
  }
`;

const RightSection = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;

  @media screen and (max-width: 768px) {
    width: 100%;
    justify-content: flex-end;
  }
`;

const RibbonGlow = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  pointer-events: none;
  opacity: 0;
  transition: opacity 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);

  background: ${({ variant }) => {
    switch (variant) {
      case "unaffiliated":
        return "radial-gradient(ellipse at center, rgba(149, 165, 166, 0.1) 0%, transparent 70%)";
      case "subtle":
        return "radial-gradient(ellipse at center, rgba(255, 255, 255, 0.1) 0%, transparent 70%)";
      case "primary":
      default:
        return "radial-gradient(ellipse at center, rgba(44, 90, 160, 0.1) 0%, transparent 70%)";
    }
  }};

  ${RibbonContainer}:hover & {
    opacity: 1;
  }
`;

export default SectionRibbon;
